import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, fromEvent, interval, merge, startWith, Subscription } from "rxjs";
import { formatHMS, formatS } from "../utils/time-utils";
import { AuthService } from "./auth.service";
import { environment } from "src/environments/environment.generated";
import { Location } from "@angular/common";

const IDLE_MS = 4 * 60_000;
// const IDLE_MS = 10_000;
const GRACE_MS = 62_000;
// const GRACE_MS = 5_000;

@Injectable({ providedIn: 'root' })
export class SessionTimerService implements OnDestroy {
    private tokenExpiryTimeLeft?: number; // time until token expires
    private tickSub?: Subscription; // emit every 1 second; animate clock

    private activitySub?: Subscription; // to detect user activity
    private idleTimeLeft: number = 0; // counter idle time
    private graceTimeLeft: number = 0; // counter grace timer
    readonly graceTimeLeft$ = new BehaviorSubject<number>(0); // display grace time left in a component
    private isIdleFlag = false; // keep previous value
    private isGraceFlag = false; // keep previous value
    private graceWarnSubject$ = new BehaviorSubject<boolean>(false); // open popup in a component
    readonly graceWarn$ = this.graceWarnSubject$.asObservable(); // encapsulate

    constructor(private auth: AuthService, private location: Location) {
        this.auth.isAuthenticated$.subscribe(isAuth => {
            if (isAuth) {
                this.init(this.auth.tokenExpiryMs);
            } else {
                this.stopTimer();
            }
        });

        if (environment.mock && this.location.path() != '/login') {
            this.init(Date.now() + 600_000);
        }

        this.activitySub = merge(
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'keydown'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'touchstart')
        ).pipe(startWith(null)).subscribe(() => this.onActivity());
    }

    init(expiryTimeMs: number): void {
        this.tokenExpiryTimeLeft = expiryTimeMs;
        this.startTimer();
    }

    ngOnDestroy(): void {
        this.stopTimer();
        this.activitySub?.unsubscribe();
        this.activitySub = undefined;
    }

    private startTimer(): void {
        this.stopTimer();
        if (!this.tokenExpiryTimeLeft) {
            return;
        }
        // if (!environment.mock || !environment.production) console.log('INIT IDLE TIME LEFT');
        this.initIdleTimer();
        this.tick();
        // animate clock
        this.tickSub = interval(1000).subscribe(() => this.tick());
    }

    private stopTimer(): void {
        this.tickSub?.unsubscribe();
        this.tickSub = undefined;
        this.graceWarnSubject$.next(false);
    }

    private tick(): void {
        const tokenMsLeft = (this.tokenExpiryTimeLeft ?? Date.now()) - Date.now();
        // if (!environment.mock || !environment.production) console.log(`token expiry time left: ${formatHMS(tokenMsLeft)}`);
        if (tokenMsLeft <= 0) {
            this.stopAndLogout();
        }

        if (this.idleTimeLeft) {
            this.idleTimeLeft = this.idleTimeLeft - 1000;
            // if (!environment.mock || !environment.production) console.log(`idle time left: ${formatHMS(this.idleTimeLeft)}`);
        }
        if (this.idleTimeLeft <= 0 && this.isIdleFlag) {
            // if (!environment.mock || !environment.production) console.log(`idle time is OVER, showing popup, grace time started`);
            this.isIdleFlag = false;
            this.initGraceTimer();
        }

        if (this.graceTimeLeft) {
            this.graceTimeLeft = this.graceTimeLeft - 1000;
            this.graceTimeLeft$.next(this.graceTimeLeft);
            // if (!environment.mock || !environment.production) console.log(`grace time left: ${formatS(this.graceTimeLeft)} seconds`);
        }
        if (this.graceTimeLeft <= 0 && this.isGraceFlag) {
            // if (!environment.mock || !environment.production) console.log(`grace time is OVER, logging out!`);
            this.isGraceFlag = false;
            this.stopAndLogout();
        }
    }

    private onActivity(): void {
        // if (!environment.mock || !environment.production) console.log('activity detected');
        this.initIdleTimer();
        this.cancelGraceTimer();
    }

    private initIdleTimer(): void {
        this.idleTimeLeft = IDLE_MS;
        this.isIdleFlag = true;
    }

    private initGraceTimer(): void {
        this.graceTimeLeft = GRACE_MS;
        this.isGraceFlag = true;
        this.graceWarnSubject$.next(true);
    }

    private cancelGraceTimer(): void {
        this.graceTimeLeft = 0;
        this.isGraceFlag = false;
        this.graceWarnSubject$.next(false);
    }

    public stopAndLogout(): void {
        this.stopTimer();
        this.auth.logout();
    }
}